import React, { useState, useEffect } from 'react';
import { User, Shield, Bell, ArrowLeft, Code, Copy, Check, MessageSquare, Eye, Calendar, MapPin, Settings as SettingsIcon, Save, Upload, Tag, EyeOff, DollarSign } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import WalletManagement from './WalletManagement';
import './Settings.css';

const Settings = () => {
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);
  const [selectedFramework, setSelectedFramework] = useState('iframe');
  const [embedType, setEmbedType] = useState('inline'); // 'inline' or 'widget'
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Agent settings state
  const [agentSettings, setAgentSettings] = useState({
    name: '',
    description: '',
    agent_avatar: '',
    creator_name: '',
    creator_bio: '',
    tags: [],
    visibility_status: 'public',
    monetization_model: 'free',
    monetization_price: null
  });
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [newTag, setNewTag] = useState('');

  // Fetch conversations
  const fetchConversations = async () => {
    if (!user?.agent?.id) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/agents/${user.agent.id}/conversations`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setConversations(data.conversations || []);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch conversation details
  const fetchConversationDetails = async (conversationId) => {
    if (!user?.agent?.id) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/agents/${user.agent.id}/conversations/${conversationId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSelectedConversation(data.conversation);
      }
    } catch (error) {
      console.error('Error fetching conversation details:', error);
    }
  };

  // Load conversations on component mount
  useEffect(() => {
    fetchConversations();
  }, [user?.agent?.id]);

  // Fetch agent settings
  const fetchAgentSettings = async () => {
    setSettingsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/agents/settings', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAgentSettings({
          name: data.name || '',
          description: data.description || '',
          agent_avatar: data.agent_avatar || '',
          creator_name: data.creator_name || '',
          creator_bio: data.creator_bio || '',
          tags: data.tags || [],
          visibility_status: data.visibility_status || 'public',
          monetization_model: data.monetization_model || 'free',
          monetization_price: data.monetization_price || null
        });
      }
    } catch (error) {
      console.error('Error fetching agent settings:', error);
    } finally {
      setSettingsLoading(false);
    }
  };

  // Save agent settings
  const saveAgentSettings = async () => {
    setSettingsSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/agents/settings', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(agentSettings)
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Settings saved successfully:', data.message);
        // Show success message (you could add a toast notification here)
      } else {
        const error = await response.json();
        console.error('Error saving settings:', error.error);
      }
    } catch (error) {
      console.error('Error saving agent settings:', error);
    } finally {
      setSettingsSaving(false);
    }
  };

  // Add tag
  const addTag = () => {
    if (newTag.trim() && !agentSettings.tags.includes(newTag.trim())) {
      setAgentSettings({
        ...agentSettings,
        tags: [...agentSettings.tags, newTag.trim()]
      });
      setNewTag('');
    }
  };

  // Remove tag
  const removeTag = (tagToRemove) => {
    setAgentSettings({
      ...agentSettings,
      tags: agentSettings.tags.filter(tag => tag !== tagToRemove)
    });
  };

  // Load agent settings on component mount
  useEffect(() => {
    fetchAgentSettings();
  }, []);
  
  const iframeCode = embedType === 'inline' 
    ? `<iframe 
  src="${window.location.origin}/embed.html?agentId=${user?.agent?.id || 1}" 
  width="400" 
  height="600" 
  frameBorder="0"
  style="border: 1px solid #e1e5e9; border-radius: 12px; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);"
  title="AI Agent Chat"
></iframe>`
    : `<script src="${window.location.origin}/agent-widget.js" type="module" async></script>

<div>
  <agent-chat-widget
    is-popup="true"
    agent-id="${user?.agent?.id || 1}"
  ></agent-chat-widget>
</div>`;

  const reactCode = embedType === 'inline' 
    ? `// Inline React component
const AgentChat = () => {
  return (
    <iframe 
      src="${window.location.origin}/embed.html?agentId=${user?.agent?.id || 1}"
      width="400" 
      height="600" 
      frameBorder="0"
      style={{ border: '1px solid #e1e5e9', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)' }}
      title="AI Agent Chat"
    />
  );
};

export default AgentChat;`
    : `// Floating widget React component
import React, { useEffect } from 'react';

const AgentChat = () => {
  useEffect(() => {
    // Dynamically add the script tag
    const script = document.createElement('script');
    script.src = '${window.location.origin}/agent-widget.js';
    script.type = 'module';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      // Cleanup the script when the component unmounts
      document.body.removeChild(script);
    };
  }, []);

  return (
    <div>
      <agent-chat-widget
        is-popup="true"
        agent-id="${user?.agent?.id || 1}"
      />
    </div>
  );
};

export default AgentChat;`;

  const vueCode = embedType === 'inline' 
    ? `<template>
  <iframe 
    :src="embedUrl"
    width="400" 
    height="600" 
    frameborder="0"
    :style="{ border: '1px solid #e1e5e9', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)' }"
    title="AI Agent Chat"
  />
</template>

<script>
export default {
  name: 'AgentChat',
  computed: {
    embedUrl() {
      return '${window.location.origin}/embed.html?agentId=${user?.agent?.id || 1}';
    }
  }
}
</script>`
    : `<template>
  <div>
    <agent-chat-widget
      is-popup="true"
      :agent-id="${user?.agent?.id || 1}"
    />
  </div>
</template>

<script>
export default {
  name: 'AgentChat',
  mounted() {
    // Dynamically add the script tag
    const script = document.createElement('script');
    script.src = '${window.location.origin}/agent-widget.js';
    script.type = 'module';
    script.async = true;
    document.body.appendChild(script);
  },
  beforeUnmount() {
    // Cleanup the script when the component unmounts
    const script = document.querySelector('script[src="${window.location.origin}/agent-widget.js"]');
    if (script) {
      document.body.removeChild(script);
    }
  }
}
</script>`;

  const vanillaCode = embedType === 'inline' 
    ? `<iframe 
  src="${window.location.origin}/embed.html?agentId=${user?.agent?.id || 1}"
  width="400" 
  height="600" 
  frameBorder="0"
  style="border: 1px solid #e1e5e9; border-radius: 12px; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);"
  title="AI Agent Chat"
></iframe>`
    : `<script src="${window.location.origin}/agent-widget.js" type="module" async></script>

<div>
  <agent-chat-widget
    is-popup="true"
    agent-id="${user?.agent?.id || 1}"
  ></agent-chat-widget>
</div>`;

  const getCodeForFramework = () => {
    switch (selectedFramework) {
      case 'react':
        return reactCode;
      case 'vue':
        return vueCode;
      case 'vanilla':
        return vanillaCode;
      case 'iframe':
      default:
        return iframeCode;
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(getCodeForFramework());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  return (
    <div className="settings-container" data-theme="dark">
      <div className="settings-header">
        <button 
          onClick={() => window.history.back()} 
          className="back-button"
        >
          <ArrowLeft className="back-icon" />
          Back
        </button>
      </div>

      <div className="settings-content">
        <div className="settings-section">
          <div className="section-header">
            <User className="section-icon" />
            <h2>Account</h2>
          </div>
          <div className="section-content">
            <p>Account settings coming soon...</p>
          </div>
        </div>

        {/* Wallet & Billing Section */}
        <div className="settings-section">
          <div className="section-header">
            <DollarSign className="section-icon" />
            <h2>Wallet & Billing</h2>
          </div>
          <div className="section-content">
            <WalletManagement />
          </div>
        </div>

        {/* Agent Settings Section */}
        <div className="settings-section">
          <div className="section-header">
            <SettingsIcon className="section-icon" />
            <h2>Agent Settings</h2>
          </div>
          <div className="section-content">
            {settingsLoading ? (
              <p>Loading agent settings...</p>
            ) : (
              <div className="agent-settings-form">
                {/* Basic Info */}
                <div className="form-group">
                  <h3>Basic Information</h3>
                  
                  <div className="form-row">
                    <div className="form-field">
                      <label htmlFor="agent-name">Agent Name *</label>
                      <input
                        id="agent-name"
                        type="text"
                        value={agentSettings.name}
                        onChange={(e) => setAgentSettings({...agentSettings, name: e.target.value})}
                        placeholder="Enter agent name"
                        required
                      />
                    </div>
                  </div>

                  <div className="form-field">
                    <label htmlFor="agent-description">Agent Description</label>
                    <textarea
                      id="agent-description"
                      value={agentSettings.description}
                      onChange={(e) => setAgentSettings({...agentSettings, description: e.target.value})}
                      placeholder="Describe what your agent does..."
                      rows={3}
                    />
                  </div>

                  <div className="form-field">
                    <label htmlFor="agent-avatar">Agent Avatar URL</label>
                    <input
                      id="agent-avatar"
                      type="url"
                      value={agentSettings.agent_avatar}
                      onChange={(e) => setAgentSettings({...agentSettings, agent_avatar: e.target.value})}
                      placeholder="https://example.com/avatar.png"
                    />
                    {agentSettings.agent_avatar && (
                      <div className="avatar-preview">
                        <img src={agentSettings.agent_avatar} alt="Agent avatar preview" />
                      </div>
                    )}
                  </div>
                </div>

                {/* Creator Info */}
                <div className="form-group">
                  <h3>Creator Information</h3>
                  
                  <div className="form-field">
                    <label htmlFor="creator-name">Creator Name</label>
                    <input
                      id="creator-name"
                      type="text"
                      value={agentSettings.creator_name}
                      onChange={(e) => setAgentSettings({...agentSettings, creator_name: e.target.value})}
                      placeholder="Your name or organization"
                    />
                  </div>

                  <div className="form-field">
                    <label htmlFor="creator-bio">Creator Bio</label>
                    <textarea
                      id="creator-bio"
                      value={agentSettings.creator_bio}
                      onChange={(e) => setAgentSettings({...agentSettings, creator_bio: e.target.value})}
                      placeholder="Tell users about yourself..."
                      rows={2}
                    />
                  </div>
                </div>

                {/* Discoverability */}
                <div className="form-group">
                  <h3>Discoverability & Visibility</h3>
                  
                  <div className="form-field">
                    <label htmlFor="tags">Tags</label>
                    <div className="tags-input">
                      <div className="tags-list">
                        {agentSettings.tags.map((tag, index) => (
                          <span key={index} className="tag">
                            {tag}
                            <button onClick={() => removeTag(tag)} className="tag-remove">×</button>
                          </span>
                        ))}
                      </div>
                      <div className="tag-input-row">
                        <input
                          type="text"
                          value={newTag}
                          onChange={(e) => setNewTag(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && addTag()}
                          placeholder="Add a tag..."
                        />
                        <button onClick={addTag} className="add-tag-btn">Add</button>
                      </div>
                    </div>
                  </div>

                  <div className="form-field">
                    <label htmlFor="visibility-status">Visibility Status</label>
                    <select
                      id="visibility-status"
                      value={agentSettings.visibility_status}
                      onChange={(e) => setAgentSettings({...agentSettings, visibility_status: e.target.value})}
                    >
                      <option value="public">Public - Visible in discover page</option>
                      <option value="unlisted">Unlisted - Only accessible via direct link</option>
                      <option value="private">Private - Only you can access</option>
                    </select>
                  </div>
                </div>

                {/* Monetization */}
                <div className="form-group">
                  <h3>Monetization</h3>
                  
                  <div className="form-row">
                    <div className="form-field">
                      <label htmlFor="monetization-model">Monetization Model</label>
                      <select
                        id="monetization-model"
                        value={agentSettings.monetization_model}
                        onChange={(e) => setAgentSettings({...agentSettings, monetization_model: e.target.value})}
                      >
                        <option value="free">Free</option>
                        <option value="paid">Paid</option>
                        <option value="credits">Credits</option>
                      </select>
                    </div>
                    
                    {agentSettings.monetization_model !== 'free' && (
                      <div className="form-field">
                        <label htmlFor="monetization-price">Price ($)</label>
                        <input
                          id="monetization-price"
                          type="number"
                          step="0.01"
                          min="0"
                          value={agentSettings.monetization_price || ''}
                          onChange={(e) => setAgentSettings({...agentSettings, monetization_price: parseFloat(e.target.value) || null})}
                          placeholder="0.00"
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Save Button */}
                <div className="form-actions">
                  <button 
                    onClick={saveAgentSettings} 
                    disabled={settingsSaving || !agentSettings.name.trim()}
                    className="save-button"
                  >
                    {settingsSaving ? (
                      <>
                        <div className="spinner"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save size={16} />
                        Save Settings
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="settings-section">
          <div className="section-header">
            <Shield className="section-icon" />
            <h2>Privacy & Security</h2>
          </div>
          <div className="section-content">
            <p>Privacy and security settings coming soon...</p>
          </div>
        </div>

        <div className="settings-section">
          <div className="section-header">
            <Bell className="section-icon" />
            <h2>Notifications</h2>
          </div>
          <div className="section-content">
            <p>Notification preferences coming soon...</p>
          </div>
        </div>

        <div className="settings-section">
          <div className="section-header">
            <Code className="section-icon" />
            <h2>Embed Agent</h2>
          </div>
          <div className="section-content">
            <p>Embed your AI agent on your website or blog using the code below:</p>
            
                          <div className="embed-type-selector">
                <label htmlFor="embed-type-select">Choose embed type:</label>
                <select 
                  id="embed-type-select"
                  value={embedType}
                  onChange={(e) => setEmbedType(e.target.value)}
                  className="framework-dropdown"
                >
                  <option value="inline">Inline (embedded in page)</option>
                  <option value="widget">Floating widget (bottom-right corner)</option>
                </select>
              </div>
              
              <div className="framework-selector">
                <label htmlFor="framework-select">Choose your framework:</label>
                <select 
                  id="framework-select"
                  value={selectedFramework}
                  onChange={(e) => setSelectedFramework(e.target.value)}
                  className="framework-dropdown"
                >
                  <option value="iframe">HTML iframe</option>
                  <option value="react">React Component</option>
                  <option value="vue">Vue Component</option>
                  <option value="vanilla">Vanilla JavaScript</option>
                </select>
              </div>
            
            <div className="iframe-code-container">
              <pre className="iframe-code">{getCodeForFramework()}</pre>
              <button 
                onClick={copyToClipboard}
                className="copy-button"
                title={`Copy ${selectedFramework === 'iframe' ? 'iframe' : selectedFramework === 'react' ? 'React' : selectedFramework === 'vue' ? 'Vue' : 'Vanilla JavaScript'} code to clipboard`}
              >
                {copied ? <Check size={16} /> : <Copy size={16} />}
              </button>
            </div>
            
            <div className="iframe-preview">
              <h4>Preview ({embedType === 'inline' ? 'Inline' : 'Floating Widget'} - {selectedFramework === 'iframe' ? 'iframe' : selectedFramework === 'react' ? 'React component' : selectedFramework === 'vue' ? 'Vue component' : 'Vanilla JavaScript'}):</h4>
              {embedType === 'inline' ? (
                selectedFramework === 'iframe' ? (
                  <iframe 
                    src={`${window.location.origin}/embed.html?agentId=${user?.agent?.id || 1}`}
                    width="400" 
                    height="600" 
                    frameBorder="0"
                    style={{ border: '1px solid #e1e5e9', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)' }}
                    title="AI Agent Chat"
                  />
                ) : (
                  <div className="component-preview">
                    <div className="preview-header">
                      <span className="preview-badge">{selectedFramework === 'react' ? 'React' : selectedFramework === 'vue' ? 'Vue' : 'Vanilla JS'}</span>
                      <span className="preview-text">Inline Component Preview</span>
                    </div>
                    <div className="preview-content">
                      <p>This is a preview of how your {selectedFramework === 'react' ? 'React' : selectedFramework === 'vue' ? 'Vue' : 'Vanilla JavaScript'} component will look when embedded inline in your page.</p>
                      <p>Copy the code above and integrate it into your {selectedFramework === 'react' ? 'React' : selectedFramework === 'vue' ? 'Vue' : 'JavaScript'} application.</p>
                    </div>
                  </div>
                )
              ) : (
                <div className="widget-preview">
                  <div className="preview-header">
                    <span className="preview-badge">{selectedFramework === 'react' ? 'React' : selectedFramework === 'vue' ? 'Vue' : 'Vanilla JS'}</span>
                    <span className="preview-text">Floating Widget Preview</span>
                  </div>
                  <div className="preview-content">
                    <div className="widget-demo" style={{ position: 'relative', height: '200px', background: '#f8f9fa', borderRadius: '8px', border: '2px dashed #dee2e6' }}>
                      <div style={{ position: 'absolute', bottom: '20px', right: '20px', width: '60px', height: '60px', background: '#007bff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 4px 12px rgba(0, 123, 255, 0.3)' }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                          <path d="M20 2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h4l4 4 4-4h4c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/>
                        </svg>
                      </div>
                      <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', color: '#6c757d' }}>
                        <p>Floating chat widget will appear in the bottom-right corner</p>
                        <p style={{ fontSize: '14px', marginTop: '8px' }}>Click the blue button to open chat</p>
                      </div>
                    </div>
                    <p style={{ marginTop: '12px', fontSize: '14px' }}>This floating widget will appear as a button in the bottom-right corner of your website. When clicked, it opens a chat window.</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Conversation History Section */}
        <div className="settings-section">
          <div className="section-header">
            <MessageSquare className="section-icon" />
            <h2>Conversation History</h2>
          </div>
          <div className="section-content">
            {loading ? (
              <p>Loading conversations...</p>
            ) : conversations.length === 0 ? (
              <p>No conversations yet. Conversations from embedded widgets will appear here.</p>
            ) : (
              <div className="conversations-list">
                {conversations.map((conversation) => (
                  <div 
                    key={conversation.id} 
                    className={`conversation-item ${selectedConversation?.id === conversation.id ? 'selected' : ''}`}
                    onClick={() => fetchConversationDetails(conversation.id)}
                  >
                    <div className="conversation-header">
                      <div className="conversation-title">
                        <MessageSquare className="conversation-icon" />
                        <span>{conversation.title}</span>
                      </div>
                      <div className="conversation-meta">
                        <span className="message-count">{conversation.message_count} messages</span>
                        <span className="conversation-date">
                          {new Date(conversation.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <div className="conversation-details">
                      <div className="conversation-info">
                        <Calendar className="info-icon" />
                        <span>{new Date(conversation.created_at).toLocaleString()}</span>
                      </div>
                      {conversation.user_ip && (
                        <div className="conversation-info">
                          <MapPin className="info-icon" />
                          <span>IP: {conversation.user_ip}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Conversation Details Modal */}
            {selectedConversation && (
              <div className="conversation-modal">
                <div className="modal-header">
                  <h3>Conversation Details</h3>
                  <button 
                    className="close-button"
                    onClick={() => setSelectedConversation(null)}
                  >
                    ×
                  </button>
                </div>
                <div className="modal-content">
                  <div className="conversation-meta-info">
                    <div className="meta-item">
                      <Calendar className="meta-icon" />
                      <span>Started: {new Date(selectedConversation.created_at).toLocaleString()}</span>
                    </div>
                    <div className="meta-item">
                      <MessageSquare className="meta-icon" />
                      <span>Messages: {selectedConversation.messages?.length || 0}</span>
                    </div>
                    {selectedConversation.user_ip && (
                      <div className="meta-item">
                        <MapPin className="meta-icon" />
                        <span>IP: {selectedConversation.user_ip}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="messages-container">
                    {selectedConversation.messages?.map((message, index) => (
                      <div key={index} className={`message ${message.role}`}>
                        <div className="message-header">
                          <span className="message-role">{message.role}</span>
                          <span className="message-time">
                            {new Date(message.timestamp).toLocaleTimeString()}
                          </span>
                        </div>
                        <div className="message-content">{message.content}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
